/**
 * STFFLG-4310: the Time Off History year filter, anchored on the hire date (the Staffology port of
 * SimplifyHR SHR-4302's `timeOffHistoryYears`). Shared by the admin Assignment → Time Off History
 * and the candidate My Info → Time Off History; both filter on the client, because the msp
 * returns the whole history unbounded.
 *
 * Definitions (Kasey's defaults Q16–Q18, 2026-10-23):
 * - The hire date is the msp summary's `hireDate` (the assignment's `assignmentStartDate`, else its
 *   `startDate`: the date the accrual engine accrues from), else the same fields on the assignment
 *   the page already holds. It is a UTC calendar date, 'YYYY-MM-DD'.
 * - The options run from the current year down to the hire year, newest first, then All. No year
 *   before the hire year is ever listed. A missing, invalid or future hire date counts as none,
 *   which leaves [current year, All].
 * - Year Y holds the rows dated Y-01-01 to Y-12-31, both inclusive; in the hire year that is the
 *   hire date to Dec 31. A row dated BEFORE the hire date (most often the synthetic
 *   "Policy reassignment" row, dated at the policy's creation) is filed under the hire year, with
 *   its real date, so the Accrual & Policies tab is not left empty (Q17).
 * - All shows every row: the hire date onward plus the pre-hire rows, with no upper bound, so
 *   upcoming approved leave dated in a later year stays visible (Q18).
 * - Rows are compared by their `dateIso` (the UTC date the msp sends), and the current year comes
 *   from the viewer's clock. A row with no date passes every year.
 *
 * Every function that needs today takes `now`, so the tests can pin it.
 */

/** The option value for All. Every other value is a 'YYYY' year. */
export const ALL_HISTORY_YEARS = 'all'

export interface HistoryYearOption {
  value: string
  label: string
}

const YEAR = /^\d{4}$/

const pad = (value: number): string => String(value).padStart(2, '0')

/** The viewer's calendar date, 'YYYY-MM-DD'. */
const localIsoDate = (now: Date): string =>
  `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`

/** The current year as an option value. */
export const currentHistoryYear = (now: Date = new Date()): string => String(now.getFullYear())

/**
 * The hire date as a UTC calendar date, 'YYYY-MM-DD', the way the msp words it: a date-only value
 * stays as it is, and an instant gives its UTC date ('2020-03-15T23:30:00Z' → '2020-03-15').
 * Undefined when the value is missing or invalid, or when it is later than today: a future hire
 * has no History to anchor yet (Q18).
 */
export function toHireDateIso(
  value: string | Date | null | undefined,
  now: Date = new Date(),
): string | undefined {
  if (value === null || value === undefined || value === '') return undefined
  const instant = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(instant.getTime())) return undefined
  const iso = `${instant.getUTCFullYear()}-${pad(instant.getUTCMonth() + 1)}-${pad(
    instant.getUTCDate(),
  )}`
  return iso > localIsoDate(now) ? undefined : iso
}

/** The year of a 'YYYY-MM-DD' hire date, never later than the current year. */
const hireYearOf = (hireIso: string | undefined, now: Date): number | undefined =>
  hireIso ? Math.min(Number(hireIso.slice(0, 4)), now.getFullYear()) : undefined

/**
 * The current year down to the hire year, newest first, then All. Pass the hire date through
 * `toHireDateIso` first; without one the list is [current year, All].
 */
export function buildYearOptions(
  hireIso: string | undefined,
  now: Date = new Date(),
): HistoryYearOption[] {
  const currentYear = now.getFullYear()
  const hireYear = hireYearOf(hireIso, now) ?? currentYear
  const options: HistoryYearOption[] = []
  for (let year = currentYear; year >= hireYear; year--) {
    options.push({value: String(year), label: String(year)})
  }
  options.push({value: ALL_HISTORY_YEARS, label: 'All'})
  return options
}

/**
 * Whether a History row dated `dateIso` belongs under `option` (a 'YYYY' year or All), given the
 * hire date. See the module comment for the rules; an unknown option shows every row.
 */
export function isInHistoryYear(
  dateIso: string | null | undefined,
  option: string,
  hireIso?: string,
): boolean {
  if (!dateIso || option === ALL_HISTORY_YEARS || !YEAR.test(option)) return true
  const rowDate = dateIso.slice(0, 10)
  // Before the hire date: filed under the hire year (Q17).
  const filedYear = hireIso && rowDate < hireIso ? hireIso.slice(0, 4) : rowDate.slice(0, 4)
  return filedYear === option
}

/**
 * The year the History shows: the viewer's pick while the list still offers it, else the current
 * year. Derived rather than stored, so a hire date that arrives (or changes) after the first render
 * widens the list without dropping a pick that is still offered.
 */
export function resolveHistoryYear(
  picked: string | null | undefined,
  options: HistoryYearOption[],
  now: Date = new Date(),
): string {
  return picked && options.some((option) => option.value === picked)
    ? picked
    : currentHistoryYear(now)
}
